import { useState, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  PersonOff as PersonOffIcon,
  EventBusy as EventBusyIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getTeachersDirect } from '@timetable/api';
import type { Teacher } from '@timetable/types';
import { useAuthStore } from '@/store/authStore';
import { queryKeys } from '@/lib/queryKeys';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TeacherSelectorProps {
  /** Currently selected teacher ID */
  value: string | null;
  /** Callback when selection changes */
  onChange: (teacherId: string | null) => void;
  /** Target date for checking eligibility */
  targetDate: string;
  /** Period ID for checking eligibility */
  periodId: string;
  /** List of teacher IDs who are absent on the target date */
  absentTeacherIds?: string[];
  /** List of teacher IDs who are unavailable on the target date */
  unavailableTeacherIds?: string[];
  /** List of teacher IDs who are at their daily cap */
  atDailyCapTeacherIds?: string[];
  /** List of teacher IDs who are busy (have a class) during this period */
  busyTeacherIds?: string[];
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Label for the text field */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Size of the component */
  size?: 'small' | 'medium';
}

interface TeacherOption extends Teacher {
  /** Computed eligibility flags */
  isAbsent?: boolean;
  isUnavailable?: boolean;
  isAtDailyCap?: boolean;
  isBusy?: boolean;
  /** Combined ineligibility reason for display */
  ineligibilityReason?: string;
}

// ─── Helper Functions ─────────────────────────────────────────────────────────

function getIneligibilityReason(teacher: TeacherOption): string | undefined {
  const reasons: string[] = [];
  
  if (teacher.isAbsent) reasons.push('Absent');
  if (teacher.isUnavailable) reasons.push('Unavailable');
  if (teacher.isBusy) reasons.push('Busy');
  if (teacher.isAtDailyCap) reasons.push('At daily cap');
  
  return reasons.length > 0 ? reasons.join(', ') : undefined;
}

function getIneligibilityIcon(teacher: TeacherOption) {
  if (teacher.isAbsent) return <PersonOffIcon fontSize="small" />;
  if (teacher.isUnavailable) return <EventBusyIcon fontSize="small" />;
  if (teacher.isBusy) return <ScheduleIcon fontSize="small" />;
  if (teacher.isAtDailyCap) return <WarningIcon fontSize="small" />;
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * TeacherSelector - Searchable dropdown for selecting substitute teachers
 * 
 * Features:
 * - Real-time search/filter as admin types (Req 5.1, 5.8)
 * - Shows teacher name + teacher type (Req 5.1, 5.8)
 * - Visual flags for ineligible teachers (Req 5.2)
 * - Admin can still select flagged teachers (override capability)
 * 
 * Requirements: 5.1, 5.2, 5.8
 */
export function TeacherSelector({
  value,
  onChange,
  targetDate,
  periodId,
  absentTeacherIds = [],
  unavailableTeacherIds = [],
  atDailyCapTeacherIds = [],
  busyTeacherIds = [],
  disabled = false,
  label = 'Select Teacher',
  placeholder = 'Search by name...',
  size = 'medium',
}: TeacherSelectorProps) {
  const { schoolContext } = useAuthStore();
  const [inputValue, setInputValue] = useState('');

  // Fetch all active teachers for the school
  const {
    data: teachers = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || undefined }),
    enabled: !!schoolContext,
  });

  // Enrich teachers with eligibility flags
  const teacherOptions: TeacherOption[] = useMemo(() => {
    return teachers.map((teacher) => {
      const isAbsent = absentTeacherIds.includes(teacher.id);
      const isUnavailable = unavailableTeacherIds.includes(teacher.id);
      const isAtDailyCap = atDailyCapTeacherIds.includes(teacher.id);
      const isBusy = busyTeacherIds.includes(teacher.id);

      const enriched: TeacherOption = {
        ...teacher,
        isAbsent,
        isUnavailable,
        isAtDailyCap,
        isBusy,
      };

      enriched.ineligibilityReason = getIneligibilityReason(enriched);

      return enriched;
    });
  }, [teachers, absentTeacherIds, unavailableTeacherIds, atDailyCapTeacherIds, busyTeacherIds]);

  // Find the selected teacher object
  const selectedTeacher = teacherOptions.find((t) => t.id === value) || null;

  // Handle selection change
  const handleChange = (_event: any, newValue: TeacherOption | null) => {
    onChange(newValue?.id || null);
  };

  // Handle input change (for search)
  const handleInputChange = (_event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  // Filter options based on search input (real-time filter - Req 5.8)
  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return teacherOptions;
    
    const searchLower = inputValue.toLowerCase();
    return teacherOptions.filter((teacher) =>
      teacher.name.toLowerCase().includes(searchLower)
    );
  }, [teacherOptions, inputValue]);

  // Sort: eligible teachers first, then ineligible
  const sortedOptions = useMemo(() => {
    return [...filteredOptions].sort((a, b) => {
      const aIneligible = !!a.ineligibilityReason;
      const bIneligible = !!b.ineligibilityReason;
      
      if (aIneligible === bIneligible) {
        return a.name.localeCompare(b.name);
      }
      
      return aIneligible ? 1 : -1;
    });
  }, [filteredOptions]);

  if (error) {
    return (
      <TextField
        label={label}
        value="Error loading teachers"
        error
        helperText={(error as Error).message}
        disabled
        fullWidth
        size={size}
      />
    );
  }

  return (
    <Autocomplete
      value={selectedTeacher}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={sortedOptions}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      loading={isLoading}
      disabled={disabled}
      size={size}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props as any;
        const icon = getIneligibilityIcon(option);
        const isIneligible = !!option.ineligibilityReason;

        return (
          <Box
            component="li"
            key={key}
            {...otherProps}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1,
              px: 2,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {/* Teacher name and type */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {option.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {option.teacher_type?.type_name || 'Unknown Type'}
              </Typography>
            </Box>

            {/* Ineligibility flag (Req 5.2) */}
            {isIneligible && (
              <Tooltip title={option.ineligibilityReason || ''} arrow>
                <Chip
                  icon={icon || undefined}
                  label={option.ineligibilityReason}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ maxWidth: 150 }}
                />
              </Tooltip>
            )}
          </Box>
        );
      }}
      noOptionsText={
        inputValue.trim()
          ? `No teachers found matching "${inputValue}"`
          : 'No teachers available'
      }
    />
  );
}
